// IndexedDB wrapper for offline storage
const DB_NAME = 'FabricStockDB';
const DB_VERSION = 1;

const STORES = {
  LOTS: 'lots',
  ROLLS: 'rolls',
  DISPATCHES: 'dispatches',
  OFFLINE_QUEUE: 'offlineQueue',
  USER: 'user',
  SYNC_META: 'syncMeta',
};

let db = null;

// Initialize IndexedDB
export async function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Lots store
      if (!database.objectStoreNames.contains(STORES.LOTS)) {
        const lotsStore = database.createObjectStore(STORES.LOTS, { keyPath: 'id' });
        lotsStore.createIndex('lot_no', 'lot_no', { unique: true });
      }

      // Rolls store
      if (!database.objectStoreNames.contains(STORES.ROLLS)) {
        const rollsStore = database.createObjectStore(STORES.ROLLS, { keyPath: 'id' });
        rollsStore.createIndex('lot_id', 'lot_id', { unique: false });
        rollsStore.createIndex('lot_roll', ['lot_id', 'roll_no'], { unique: true });
      }

      // Dispatches store
      if (!database.objectStoreNames.contains(STORES.DISPATCHES)) {
        const dispatchesStore = database.createObjectStore(STORES.DISPATCHES, { keyPath: 'id' });
        dispatchesStore.createIndex('lot_id', 'lot_id', { unique: false });
      }

      // Offline queue store
      if (!database.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = database.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id', autoIncrement: true });
        queueStore.createIndex('timestamp', 'timestamp', { unique: false });
        queueStore.createIndex('type', 'type', { unique: false });
      }

      // User store
      if (!database.objectStoreNames.contains(STORES.USER)) {
        database.createObjectStore(STORES.USER, { keyPath: 'key' });
      }

      // Sync metadata store
      if (!database.objectStoreNames.contains(STORES.SYNC_META)) {
        database.createObjectStore(STORES.SYNC_META, { keyPath: 'key' });
      }
    };
  });
}

// Get database instance
export async function getDB() {
  if (!db) {
    await initDB();
  }
  return db;
}

// Generic CRUD operations
export async function dbGet(storeName, key) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function dbGetAll(storeName) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function dbPut(storeName, data) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function dbDelete(storeName, key) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function dbClear(storeName) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function dbGetByIndex(storeName, indexName, value) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

// ============ LOTS ============
export async function getLocalLots() {
  return dbGetAll(STORES.LOTS);
}

export async function getLocalLot(lotId) {
  return dbGet(STORES.LOTS, lotId);
}

export async function saveLocalLots(lots) {
  for (const lot of lots) {
    await dbPut(STORES.LOTS, lot);
  }
}

export async function saveLocalLot(lot) {
  return dbPut(STORES.LOTS, lot);
}

// ============ ROLLS ============
export async function getLocalRolls(lotId) {
  return dbGetByIndex(STORES.ROLLS, 'lot_id', lotId);
}

export async function getLocalRoll(rollId) {
  return dbGet(STORES.ROLLS, rollId);
}

export async function saveLocalRolls(rolls) {
  for (const roll of rolls) {
    await dbPut(STORES.ROLLS, roll);
  }
}

export async function saveLocalRoll(roll) {
  return dbPut(STORES.ROLLS, roll);
}

export async function deleteLocalRoll(rollId) {
  return dbDelete(STORES.ROLLS, rollId);
}

// Check if roll_no exists in lot (for conflict detection)
export async function rollExistsInLot(lotId, rollNo) {
  const rolls = await getLocalRolls(lotId);
  return rolls.some(r => r.roll_no === rollNo);
}

// ============ DISPATCHES ============
export async function getLocalDispatches() {
  return dbGetAll(STORES.DISPATCHES);
}

export async function saveLocalDispatches(dispatches) {
  for (const dispatch of dispatches) {
    await dbPut(STORES.DISPATCHES, dispatch);
  }
}

export async function saveLocalDispatch(dispatch) {
  return dbPut(STORES.DISPATCHES, dispatch);
}

// ============ OFFLINE QUEUE ============
export async function addToOfflineQueue(action) {
  const queueItem = {
    ...action,
    timestamp: Date.now(),
    synced: false,
    retryCount: 0,
  };
  return dbPut(STORES.OFFLINE_QUEUE, queueItem);
}

export async function getOfflineQueue() {
  const items = await dbGetAll(STORES.OFFLINE_QUEUE);
  return items.filter(item => !item.synced).sort((a, b) => a.timestamp - b.timestamp);
}

export async function markQueueItemSynced(id) {
  const item = await dbGet(STORES.OFFLINE_QUEUE, id);
  if (item) {
    item.synced = true;
    item.syncedAt = Date.now();
    await dbPut(STORES.OFFLINE_QUEUE, item);
  }
}

export async function markQueueItemFailed(id, error) {
  const item = await dbGet(STORES.OFFLINE_QUEUE, id);
  if (item) {
    item.retryCount = (item.retryCount || 0) + 1;
    item.lastError = error;
    item.lastRetry = Date.now();
    await dbPut(STORES.OFFLINE_QUEUE, item);
  }
}

export async function clearSyncedQueue() {
  const items = await dbGetAll(STORES.OFFLINE_QUEUE);
  for (const item of items) {
    if (item.synced) {
      await dbDelete(STORES.OFFLINE_QUEUE, item.id);
    }
  }
}

// ============ USER ============
export async function saveLocalUser(user, token) {
  await dbPut(STORES.USER, { key: 'currentUser', user, token });
}

export async function getLocalUser() {
  return dbGet(STORES.USER, 'currentUser');
}

export async function clearLocalUser() {
  return dbDelete(STORES.USER, 'currentUser');
}

// ============ SYNC META ============
export async function setLastSyncTime(time = Date.now()) {
  return dbPut(STORES.SYNC_META, { key: 'lastSync', time });
}

export async function getLastSyncTime() {
  const meta = await dbGet(STORES.SYNC_META, 'lastSync');
  return meta?.time || 0;
}

export async function setSyncStatus(status) {
  return dbPut(STORES.SYNC_META, { key: 'syncStatus', status, updatedAt: Date.now() });
}

export async function getSyncStatus() {
  const meta = await dbGet(STORES.SYNC_META, 'syncStatus');
  return meta?.status || 'idle';
}

// ============ UTILITY ============
export async function clearAllLocalData() {
  await dbClear(STORES.LOTS);
  await dbClear(STORES.ROLLS);
  await dbClear(STORES.DISPATCHES);
  await dbClear(STORES.OFFLINE_QUEUE);
  await dbClear(STORES.SYNC_META);
}

export { STORES };
